import { noop } from 'lodash';
import { useMemo } from 'react';

import { ConfidentialAccessLevel } from '@dreryk/egabinet-medical-data-confidentiality-frontend-api';

import { Translate, TranslateDeclension } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   EditIcon,
   IconSize,
   IconWrapper,
   JustifyContent,
   Link,
   TableColumnConfiguration,
   Text,
   ValueRendererProps,
} from '@dreryk/react-components';

import { generalSettingsTranslationKeys } from '../../../translations';

import { ConfidentialitySettingRow } from './ConfidentialitySettings.types';

const t = generalSettingsTranslationKeys.confidentialitySettings;

type UseConfidentialitySettingsColumnsProps = {
   onEdit: VoidFunction;
};

export const useConfidentialitySettingsColumns = (
   props: UseConfidentialitySettingsColumnsProps
): TableColumnConfiguration<ConfidentialitySettingRow>[] => {
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         securityLevel: '55%',
         accessDuration: '45%',
         actions: '40px',
      });

      return [
         {
            name: 'securityLevel',
            label: t.table.securityLevel,
            width: columnWidths.securityLevel,
            accessor: noop,
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<ConfidentialitySettingRow>) => (
               <Text testId={`securityLevel-${bean.accessLevel}`}>
                  <Translate>{t.securityLevelMap[bean.accessLevel]}</Translate>
               </Text>
            ),
         },
         {
            name: 'accessDuration',
            label: t.table.accessDuration,
            width: columnWidths.accessDuration,
            accessor: noop,
            sortingDisabled: true,
            renderer: ({ bean }: ValueRendererProps<ConfidentialitySettingRow>) => (
               <Text testId={`accessDuration-${bean.accessLevel}`}>
                  {bean.durationHours}{' '}
                  <TranslateDeclension
                     count={bean.durationHours}
                     accusative={t.hoursDeclensionMap.accusative}
                     genitive={t.hoursDeclensionMap.genitive}
                     plural={t.hoursDeclensionMap.plural}
                  />
               </Text>
            ),
         },
         {
            name: 'actions',
            label: '',
            width: columnWidths.actions,
            accessor: noop,
            sortingDisabled: true,
            justifyContent: JustifyContent.END,
            renderer: ({ bean }: ValueRendererProps<ConfidentialitySettingRow>) => (
               <Link
                  onClick={props.onEdit}
                  testId={`editConfidentiality-${bean.accessLevel}`}
                  nowrap
                  icon={
                     <IconWrapper testId={'edit'} size={IconSize.SMALL}>
                        <EditIcon />
                     </IconWrapper>
                  }
               />
            ),
         },
      ];
   }, [props.onEdit]);
};

export const useConfidentialitySettingRows = (
   accessLevels: ConfidentialAccessLevel[],
   getDurationHours: (accessLevel: ConfidentialAccessLevel) => number
): ConfidentialitySettingRow[] =>
   useMemo(
      () =>
         accessLevels.map((accessLevel) => ({
            accessLevel: accessLevel,
            durationHours: getDurationHours(accessLevel),
         })),
      [accessLevels, getDurationHours]
   );
